import { Card, Flex, Typography } from 'antd'
import { Building2, Layers, UserPlus, Users as UsersIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from 'src/components'

const { Text, Title } = Typography

const CHIP_PADDING = '10px 12px'

type Props = {
  divisions: number
  departments: number
  teams: number
  membersAssigned: number
  totalMembers?: number
}

const KpiChip = ({
  label,
  value,
  icon,
  footer
}: {
  label: string
  value: ReactNode
  icon: ReactNode
  footer?: string
}) => (
  <Card variant="outlined" styles={{ body: { padding: CHIP_PADDING } }}>
    <Flex align="center" gap={10} style={{ minWidth: 0 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 6,
          background: 'var(--ant-color-primary-bg)',
          color: 'var(--ant-color-primary-text)',
          flex: '0 0 auto'
        }}
      >
        {icon}
      </span>
      <Flex vertical gap={1} style={{ lineHeight: 1.2, minWidth: 0 }}>
        <SectionLabel>{label}</SectionLabel>
        <Flex align="baseline" gap={6} wrap="wrap">
          <Title
            level={5}
            style={{
              margin: 0,
              fontSize: 16,
              lineHeight: 1.1,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {value}
          </Title>
          {footer ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {footer}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  </Card>
)

export const OrgKpiChips = ({
  divisions,
  departments,
  teams,
  membersAssigned,
  totalMembers
}: Props) => {
  const { t } = useTranslation()

  return (
    <Flex gap={8} wrap="wrap">
      <KpiChip
        label={t('organization.kpis.divisions')}
        value={divisions}
        icon={<Building2 size={16} />}
      />
      <KpiChip
        label={t('organization.kpis.departments')}
        value={departments}
        icon={<Layers size={16} />}
      />
      <KpiChip
        label={t('organization.kpis.teams')}
        value={teams}
        icon={<UsersIcon size={16} />}
      />
      <KpiChip
        label={t('organization.kpis.membersAssigned')}
        value={membersAssigned}
        icon={<UserPlus size={16} />}
        footer={
          totalMembers != null
            ? t('organization.kpis.of', { total: totalMembers })
            : undefined
        }
      />
    </Flex>
  )
}
